import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Row, 
  Col, 
  Modal, 
  Card, 
  Input, 
  Space, 
  Typography, 
  message,
  Alert
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { 
  LockOutlined, 
  MailOutlined, 
  UserOutlined, 
  SafetyOutlined,
  SendOutlined,
  MessageOutlined,
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import API from '../services/api';
import './UserSettings.css';
import usePermissionCheck from '../hooks/usePermissionCheck';
import PermissionDenied from '../components/PermissionDenied';
import { clearAuthData } from '../utils/auth';

const { Title, Text } = Typography;

const UserSettings = () => {
  const { hasPermission, isLoading: permissionLoading, userAccountType } = usePermissionCheck('canModifySettings');
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newQq, setNewQq] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalError, setModalError] = useState(false);
  const [logoutOnClose, setLogoutOnClose] = useState(false);
  const [userData, setUserData] = useState({});
  const [loading, setLoading] = useState({
    password: false,
    email: false,
    qq: false
  });
  const navigate = useNavigate();

  // 在组件加载时获取用户信息
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await API.get('/api/user/profile');
        setUserData(response.data);
      } catch (error) {
        const errorMessage = error.response?.data?.message || '无法获取用户信息';
        message.error(errorMessage);
      }
    };

    fetchUserData();
  }, []);

  // 权限检查 - 移到所有hooks之后
  if (!hasPermission) {
    return (
      <PermissionDenied 
        isLoading={permissionLoading}
        permissionName="账号设置"
        userAccountType={userAccountType}
      />
    );
  }

  // 显示弹窗，关闭时刷新页面或退出登录
  const showModalMessage = (message, isError = false) => {
    setModalMessage(message);
    setModalError(isError);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (logoutOnClose) {
      clearAuthData();
      setLogoutOnClose(false);
      navigate('/login', { replace: true });
      return;
    }

    window.location.reload();
  };

  // 处理密码修改
  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      message.error('请填写完整的密码信息');
      return;
    }
    
    setLoading(prev => ({ ...prev, password: true }));
    try {
      const response = await API.post('/api/user/change-password', {
        currentPassword,
        newPassword,
      });
      setLogoutOnClose(true);
      showModalMessage(response.data.message);
      setCurrentPassword('');
      setNewPassword('');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '修改密码失败';
      setLogoutOnClose(false);
      showModalMessage(errorMessage, true);
    } finally {
      setLoading(prev => ({ ...prev, password: false }));
    }
  };

  // 处理邮箱修改
  const handleChangeEmail = async () => {
    if (!newEmail) {
      message.error('请输入新邮箱地址');
      return;
    }
    
    setLoading(prev => ({ ...prev, email: true }));
    try {
      const response = await API.post('/api/user/change-email', { newEmail });
      setLogoutOnClose(false);
      showModalMessage(response.data.message);
      setNewEmail('');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '修改邮箱失败';
      setLogoutOnClose(false);
      showModalMessage(errorMessage, true);
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  };

  // 处理QQ号修改
  const handleChangeQq = async () => {
    if (!newQq) {
      message.error('请输入新QQ号');
      return;
    }
    
    setLoading(prev => ({ ...prev, qq: true }));
    try {
      const response = await API.post('/api/user/change-qq', { newQq });
      setLogoutOnClose(false);
      showModalMessage(response.data.message);
      setNewQq('');
    } catch (error) {
      const errorMessage = error.response?.data?.message || '修改QQ号失败';
      setLogoutOnClose(false);
      showModalMessage(errorMessage, true);
    } finally {
      setLoading(prev => ({ ...prev, qq: false }));
    }
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* 页面标题 */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ color: '#1890ff', margin: 0 }}>
            <UserOutlined style={{ marginRight: '8px' }} />
            用户设置
          </Title>
        </div>

        {/* 用户信息卡片 */}
        <Card style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
              当前账户信息
            </Title>
            <Text type="secondary">查看您的基本信息</Text>
          </div>
          
          <Row gutter={[24, 16]} justify="center">
            <Col xs={24} sm={8}>
              <Card 
                size="small" 
                style={{ textAlign: 'center', backgroundColor: '#fafafa' }}
              >
                <Space direction="vertical" size="small">
                  <UserOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                  <div>
                    <Text strong>用户名</Text>
                    <br />
                    <Text>{userData.username || '未设置'}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card 
                size="small" 
                style={{ textAlign: 'center', backgroundColor: '#fafafa' }}
              >
                <Space direction="vertical" size="small">
                  <MailOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                  <div>
                    <Text strong>邮箱地址</Text>
                    <br />
                    <Text>{userData.email || '未设置'}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card 
                size="small" 
                style={{ textAlign: 'center', backgroundColor: '#fafafa' }}
              >
                <Space direction="vertical" size="small">
                  <MessageOutlined style={{ fontSize: '24px', color: '#faad14' }} />
                  <div>
                    <Text strong>QQ号码</Text>
                    <br />
                    <Text>{userData.qq || '未设置'}</Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </Card>

        {/* 设置选项 */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px' }}>账户设置</Title>
          <Row gutter={[24, 24]}>
            {/* 修改密码 */}
            <Col xs={24} md={8}>
              <Card 
                title={
                  <Space>
                    <LockOutlined style={{ color: '#1890ff' }} />
                    修改密码
                  </Space>
                }
                style={{ height: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text strong>当前密码</Text>
                    <Input.Password
                      placeholder="输入当前密码"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      prefix={<LockOutlined />}
                    />
                  </div>
                  <div>
                    <Text strong>新密码</Text>
                    <Input.Password
                      placeholder="输入新密码"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      prefix={<LockOutlined />}
                    />
                  </div>
                                     <Button 
                     type="primary" 
                     onClick={handleChangePassword}
                     loading={loading.password}
                     icon={<SafetyOutlined />}
                     block
                   >
                     {loading.password ? '修改中...' : '修改密码'}
                   </Button>
                </Space>
              </Card>
            </Col>

            {/* 修改邮箱 */}
            <Col xs={24} md={8}>
              <Card 
                title={
                  <Space>
                    <MailOutlined style={{ color: '#52c41a' }} />
                    修改邮箱
                  </Space>
                }
                style={{ height: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text strong>新邮箱地址</Text>
                    <Input
                      type="email"
                      placeholder="输入新邮箱"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      prefix={<MailOutlined />}
                    />
                  </div>
                  <Button 
                    type="primary" 
                    onClick={handleChangeEmail}
                    loading={loading.email}
                    icon={<SendOutlined />}
                    block
                    style={{ marginTop: 'auto' }}
                  >
                    {loading.email ? '修改中...' : '修改邮箱'}
                  </Button>
                </Space>
              </Card>
            </Col>

            {/* 修改QQ号 */}
            <Col xs={24} md={8}>
              <Card 
                title={
                  <Space>
                    <MessageOutlined style={{ color: '#faad14' }} />
                    修改QQ号
                  </Space>
                }
                style={{ height: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text strong>新QQ号</Text>
                    <Input
                      placeholder="输入新的QQ号"
                      value={newQq}
                      onChange={(e) => setNewQq(e.target.value)}
                      prefix={<MessageOutlined />}
                    />
                  </div>
                  <Button 
                    type="primary" 
                    onClick={handleChangeQq}
                    loading={loading.qq}
                    icon={<MessageOutlined />}
                    block
                    style={{ marginTop: 'auto' }}
                  >
                    {loading.qq ? '修改中...' : '修改QQ号'}
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>

        {/* 底部重要提示 */}
                 <Alert
           message="重要提示"
           description="请确保您的邮箱地址正确且能够正常接收邮件。邮箱地址将用于密码找回等重要功能，错误的邮箱地址可能导致您无法找回密码或接收重要的安全提醒。"
           type="warning"
           icon={<WarningOutlined />}
           showIcon
         />

        {/* 结果模态框 */}
        <Modal
                     title={
             <Space>
               {modalError ? (
                 <WarningOutlined style={{ color: '#ff4d4f' }} />
               ) : (
                 <CheckCircleOutlined style={{ color: '#52c41a' }} />
               )}
               {modalError ? '操作失败' : '操作成功'}
             </Space>
           }
          open={showModal}
          onCancel={handleCloseModal}
          footer={[
            <Button 
              key="ok"
              type={modalError ? 'danger' : 'primary'}
              onClick={handleCloseModal}
              icon={<CheckCircleOutlined />}
            >
              确定
            </Button>
          ]}
          centered
        >
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Text style={{ fontSize: '16px' }}>{modalMessage}</Text>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default UserSettings;


